import React, { useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectorCurrentClone } from "src/redux/currentClone/selector";
import { getCurrentCloneAsync } from "src/redux/currentClone/actions";
import { HeaderClone } from "src/page-components/clones/[clone]/HeaderClone";
import { TitledEditorView } from "src/page-components/clones/[clone]/TitledEditorView";
import { getPropertiesClone } from "src/page-components/clones/[clone]/constants";
import { cloneItemTitlesTrns } from "src/page-components/clones/[clone]/trns";
import { PresentationCard } from "src/page-components/clones/[clone]/PresentationCard";
import { Loader } from "src/components/Loader";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { PropertiesCard } from "src/components/Properties/PropertiesCard";
import { BreadCrumbs } from "src/components/BreadCrumbs";
import { breadCrumbsTrns } from "src/components/BreadCrumbs/trns";
import { IBreadCrumbsItem } from "src/components/BreadCrumbs/types";
import { PATHNAMES } from "src/constants/routes";
import { Sizes } from "src/@types/sizes";
import { useExtractId } from "src/hooks/useExractId";
import { MetadataWrapper } from "src/components/MetadataWrapper";

const CloneItem: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const id = useExtractId();

  const { item, isLoading } = useAppSelector(selectorCurrentClone);

  useEffect(() => {
    dispatch(getCurrentCloneAsync({ id }));
  }, [dispatch, id]);

  const breadCrumbsItems: IBreadCrumbsItem[] = [
    { title: { i18nKey: breadCrumbsTrns.home }, path: PATHNAMES.HOME },
    { title: { i18nKey: breadCrumbsTrns.clones }, path: PATHNAMES.CLONES },
    { title: { i18nKey: item?.company_name } },
  ];

  if (!item && !isLoading) return null;

  return (
    <MetadataWrapper
      title={item?.clone_meta_title}
      description={item?.clone_meta_description}
    >
      <PageWrapper mainClassName="pt-10 pb-25 mt-19">
        {isLoading ? (
          <div className="flex justify-center items-center min-h-72">
            <Loader size={Sizes.XXL} />
          </div>
        ) : (
          <>
            <section className="container flex flex-col gap-5 gl:gap-10">
              <BreadCrumbs
                items={breadCrumbsItems}
                className="hidden gl:block"
              />

              <HeaderClone {...item} />

              <div className="flex flex-col lg:flex-row items-start gap-x-10 gap-y-5">
                <PropertiesCard
                  className="border border-black-base/20 rounded-20 min-w-full lg:min-w-100 p-5"
                  properties={getPropertiesClone(item)}
                />

                <div className="flex flex-1 w-full flex-col gap-5 overflow-hidden">
                  <TitledEditorView
                    title={t(cloneItemTitlesTrns.cloneDescription)}
                    description={item.describe_product}
                  />
                  <TitledEditorView
                    title={t(cloneItemTitlesTrns.mainAdvantages)}
                    description={item.advantages}
                  />
                  <TitledEditorView
                    title={t(cloneItemTitlesTrns.companyActivities)}
                    description={item.activities}
                  />
                  <TitledEditorView
                    title={t(cloneItemTitlesTrns.additionalInformation)}
                    description={item.additional_information}
                  />

                  <div className="flex flex-col gap-3">
                    <h2>{t(cloneItemTitlesTrns.presentationTitle)}</h2>
                    <div className="grid grid-cols-auto-fill place-content-center gap-5">
                      {item.company_presentation.map((presentation) => (
                        <PresentationCard
                          key={presentation.file}
                          {...presentation}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </PageWrapper>
    </MetadataWrapper>
  );
};
export default CloneItem;
